import { Component, createRef } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { parse } from 'query-string';
import cn from 'classnames';
import ExecutionEnvironment from 'exenv';

import { onEvent } from 'helpers/EventHelpers';
import { getHmdSurveyMarkupData, submitHmdSurvey } from 'actions/ask';
import { setResponseStatus } from 'actions/client';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import StarRatingRadio from 'components/common/StarRatingRadio';
import css from 'styles/containers/hmdSurvey.scss';
import { trackEvent } from 'helpers/analytics';

const FORM_INPUT_PREFIX = 'ces_1_';

const STAR_VALUE_MAP = {
  '1': 'Very Poor',
  '2': 'Poor',
  '3': 'Average',
  '4': 'Good',
  '5': 'Excellent'
};

export class HmdSurvey extends Component {
  static fetchDataOnServer(store, location) {
    const { p: pollId, key } = parse(location.search);
    if (pollId && key) {
      return store.dispatch(getHmdSurveyMarkupData({ pollId, key }));
    } else {
      return store.dispatch(setResponseStatus(404));
    }
  }

  constructor() {
    super();
    this.errorElement = createRef();
  }

  state = {
    formInputs: null,
    jsEnabled: false,
    errorMsg: null
  }

  componentDidMount() {
    const { getHmdSurveyMarkupData, location: { search }, survey: { content }, trackEvent } = this.props;
    const { p: pollId, key } = parse(search);
    if (!content && pollId && key) {
      getHmdSurveyMarkupData({ pollId, key });
    }

    if (content) {
      this.getElementsForInjection();
      trackEvent('TE_HMD_SURVEY_LOADED');
    }

    onEvent(window, 'submit', this.handleFormSubmit, null, this);
    onEvent(window, 'click', this.handleSubmitClick, null, this);

    this.setState({ jsEnabled: true });
  }

  componentDidUpdate(prevProps) {
    const { survey: { content } } = this.props;
    if (content && content !== prevProps.survey.content) {
      this.getElementsForInjection();
    }
  }

  handleSubmitClick = ({ target }) => {
    /*
      This form has non-submit buttons that look like:
      <a href="javascript:#"/>
    */
    const link = target.closest('a');
    if (link?.href === 'javascript:#') {
      const form = target.closest('form');
      if (form) {
        this.handleFormSubmit(form);
      }
    }
  }

  handleFormSubmit = formOrEvent => {
    const { submitHmdSurvey, location: { search } } = this.props;

    // handling if a natural submit event or if we are passing <form> el via click
    const isEvent = formOrEvent.type === 'submit';
    const form = isEvent ? formOrEvent.target : formOrEvent;
    if (isEvent) {
      formOrEvent.preventDefault();
    }

    if (form.checkValidity()) {
      const els = Array.from(form.elements);
      const formData = { answers: {} };

      els.forEach(({ name, value }) => {
        if (name.includes('ces_1') || name.includes('ces_0')) {// NOT ces_nextcontent
          formData.answers[name] = value;
        } else {
          formData[name] = value;
        }
      });

      const { p: pollId, key } = parse(search);
      formData.key = key;
      formData.pollId = pollId;

      this.setState({ errorMsg: null });
      submitHmdSurvey(formData);
    } else {
      this.scrollAndSetError();
    }
  }

  scrollAndSetError = () => {
    this.setState({ errorMsg: 'Please answer all of the questions before submitting.' }, () => {
      this.errorElement.current?.scrollIntoView();
    });
  }

  getElementsForInjection = () => {
    const container = document.querySelector('[data-hmd-survey]');
    const inputs = container ? container.querySelectorAll(`[name^="${FORM_INPUT_PREFIX}"]`) : null;
    const formInputs = [];
    if (inputs) {
      for (let i = 0; i < inputs.length; i++) {
        const input = inputs[i], parent = input.parentNode;
        formInputs.push({
          element: input,
          parent: parent,
          injectTargetNode: parent.querySelector('.arui_starRating') || parent.querySelector('.yesscript'),
          copyNode: parent.querySelector('.swSprite_description'),
          name: input.name,
          value: ''
        });
      }
      this.setState({ formInputs });
    }
  }

  handleRadioChange = ({ target: { value, dataset: { indexId } } }) => {
    const i = parseInt(indexId);
    const { formInputs } = this.state;
    const newFormInputs = [...formInputs];
    const updatedInput = newFormInputs[i];

    newFormInputs[i] = {
      ...updatedInput,
      value: value
    };
    this.setState({ formInputs: newFormInputs });

    // Set copy based on star rating
    const { copyNode } = updatedInput;
    if (copyNode) {
      copyNode.innerText = STAR_VALUE_MAP[value];
    }

    // Set hidden input
    const hiddenInput = updatedInput.element;
    if (hiddenInput) {
      hiddenInput.value = value;
    }
  }

  injectStarInputs = () => {
    const { formInputs } = this.state;
    const makePortal = this.props.createPortal || createPortal; // for testmocking
    if (ExecutionEnvironment.canUseDOM && formInputs) {
      return formInputs.map(({ injectTargetNode, name }, indexId) =>
        makePortal(<StarRatingRadio
          onChange={this.handleRadioChange}
          indexId={indexId}
          name={name}
          required={true}
        />, injectTargetNode)
      );
    }
  }

  render() {
    const { survey: { content } } = this.props;
    const { jsEnabled, errorMsg } = this.state;
    if (content) {
      return (
        <SiteAwareMetadata>
          <div
            data-hmd-survey
            // Linter keeps saying jsEnabled is undefined but it is
            // eslint-disable-next-line css-modules/no-undef-class
            className={cn(css.container, { [css.jsEnabled]: jsEnabled })}>
            {errorMsg && <p ref={this.errorElement} className={css.errorMsg}>{errorMsg}</p>}
            <div
              dangerouslySetInnerHTML={{
                __html: content
              }}/>
            {this.injectStarInputs()}
          </div>
        </SiteAwareMetadata>
      );
    }

    return (
      <SiteAwareMetadata>
        <div className={css.container}>
          <p>Oops, something went wrong! Please try again.</p>
        </div>
      </SiteAwareMetadata>
    );
  }
}

function mapStateToProps(state) {
  return {
    survey: state.hmdSurvey,
    location: state.routing.locationBeforeTransitions
  };
}

HmdSurvey.defaultProps = {
  trackEvent
};

export default connect(mapStateToProps,
  {
    getHmdSurveyMarkupData,
    submitHmdSurvey
  })(HmdSurvey);
